import { createApi } from './lib/api.js';
import { createCalendar } from './lib/calendar.js';
import {
	escapeHtml,
	formatDateLabel,
	formatTimeLabel,
	formatShortDateLabel,
	todayInTimezone,
} from './lib/utils.js';

/**
 * Customer self-service: lookup booking by ID + email, edit or cancel.
 */
export function initManageBooking(root, cfg) {
	const api = createApi(cfg);
	let config = {
		max_advance_booking_days: 90,
		same_day_bookings: true,
		min_advance_booking_hours: 1,
		timezone: cfg.timezone || '',
		timezone_label: cfg.timezoneLabel || '',
	};

	let step = 'lookup';
	let booking = null;
	let permissions = null;
	let verifiedEmail = '';
	let editDate = '';
	let editTime = '';
	let slots = [];
	let slotsLoading = false;
	let loading = false;
	let error = '';
	let success = '';
	let unavailableDates = {};
	let monthAvailabilityKey = '';
	let calendarWidget = null;

	loadConfig();

	function loadConfig() {
		api.get('/public/config')
			.then((data) => {
				config = { ...config, ...data };
			})
			.catch(() => {});
	}

	function todayStr() {
		return config.timezone ? todayInTimezone(config.timezone) : todayInTimezone();
	}

	function isDateDisabled(dateStr) {
		if (dateStr < todayStr()) {
			return true;
		}
		if (config.same_day_bookings === false && dateStr === todayStr()) {
			return true;
		}

		const minHours = Number(config.min_advance_booking_hours) || 0;
		if (minHours > 0) {
			const cutoff = new Date(Date.now() + minHours * 3600000);
			const tz = config.timezone;
			const cutoffStr = tz
				? new Intl.DateTimeFormat('en-CA', {
						timeZone: tz,
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
					}).format(cutoff)
				: cutoff.getFullYear() +
					'-' +
					String(cutoff.getMonth() + 1).padStart(2, '0') +
					'-' +
					String(cutoff.getDate()).padStart(2, '0');
			if (dateStr < cutoffStr) {
				return true;
			}
		}

		const maxDays = Number(config.max_advance_booking_days) || 90;
		const max = new Date();
		max.setDate(max.getDate() + maxDays);
		const parts = dateStr.split('-').map(Number);
		const d = new Date(parts[0], parts[1] - 1, parts[2]);
		if (d > max) {
			return true;
		}

		if (Object.prototype.hasOwnProperty.call(unavailableDates, dateStr)) {
			return !!unavailableDates[dateStr];
		}

		return false;
	}

	async function loadMonthAvailability(year, month) {
		if (!booking || !year || !month) {
			return;
		}
		const key =
			booking.service_id + '-' + booking.staff_id + '-' + year + '-' + month;
		if (monthAvailabilityKey === key) {
			return;
		}
		monthAvailabilityKey = key;
		try {
			const data = await api.get('/availability/month', {
				service_id: booking.service_id,
				staff_id: booking.staff_id,
				year,
				month,
			});
			const next = { ...unavailableDates };
			(data.unavailable_dates || []).forEach((dateStr) => {
				next[dateStr] = true;
			});
			(data.available_dates || []).forEach((dateStr) => {
				next[dateStr] = false;
			});
			unavailableDates = next;
			if (editDate && unavailableDates[editDate]) {
				editDate = '';
				editTime = '';
				slots = [];
				render();
				return;
			}
			if (calendarWidget) {
				calendarWidget.refresh();
			} else if (step === 'edit') {
				render();
			}
		} catch (err) {
			monthAvailabilityKey = '';
		}
	}

	async function loadSlots() {
		if (!booking || !editDate) {
			return;
		}
		slotsLoading = true;
		slots = [];
		render();
		try {
			const data = await api.get('/availability', {
				service_id: booking.service_id,
				staff_id: booking.staff_id,
				date: editDate,
			});
			slots = (data.slots || []).filter((s) => s.available);
		} catch (err) {
			error = err.message;
		} finally {
			slotsLoading = false;
			render();
		}
	}

	function statusLabel(status) {
		return String(status || '')
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (c) => c.toUpperCase());
	}

	function lookupHtml() {
		return (
			'<div class="br-manage-card">' +
			'<h2 class="br-manage-card__title">Manage your booking</h2>' +
			'<p class="br-muted">Enter your booking ID and the email address used when you booked.</p>' +
			(error ? '<p class="br-error" role="alert">' + escapeHtml(error) + '</p>' : '') +
			'<form class="br-form br-manage-form" id="br-manage-lookup-form">' +
			'<div class="br-field">' +
			'<label for="br-manage-booking-id">Booking ID</label>' +
			'<input class="br-input" type="number" id="br-manage-booking-id" name="booking_id" min="1" required placeholder="e.g. 42" />' +
			'</div>' +
			'<div class="br-field">' +
			'<label for="br-manage-email">Email address</label>' +
			'<input class="br-input" type="email" id="br-manage-email" name="email" required autocomplete="email" />' +
			'</div>' +
			'<button type="submit" class="br-btn br-btn-primary br-btn--block"' +
			(loading ? ' disabled' : '') +
			'>Find booking</button>' +
			'</form></div>'
		);
	}

	function bookingSummaryHtml() {
		const b = booking;
		return (
			'<div class="br-manage-summary">' +
			'<p><strong>Booking #' +
			escapeHtml(b.id) +
			'</strong> · <span class="br-badge br-badge--' +
			escapeHtml(b.status) +
			'">' +
			escapeHtml(statusLabel(b.status)) +
			'</span></p>' +
			'<p>' +
			escapeHtml(b.service_name || 'Service') +
			' with ' +
			escapeHtml(b.staff_name || 'Staff') +
			'</p>' +
			'<p class="br-manage-summary__when">' +
			escapeHtml(
				b.booking_datetime_display ||
					formatDateLabel(b.booking_date, { dateFormat: config.date_format }) +
						' at ' +
						formatTimeLabel(b.booking_time, b.booking_date, {
							timeFormat: config.time_format,
						})
			) +
			'</p></div>'
		);
	}

	function actionsHtml() {
		const canEdit = permissions && permissions.can_edit;
		const canCancel = permissions && permissions.can_cancel;
		let html = '<div class="br-manage-actions">';
		if (canEdit) {
			html +=
				'<button type="button" class="br-btn br-btn-primary" data-action="edit">Change date & time</button>';
		} else if (permissions && permissions.edit_reason) {
			html +=
				'<p class="br-muted br-manage-hint">' + escapeHtml(permissions.edit_reason) + '</p>';
		}
		if (canCancel) {
			html +=
				'<button type="button" class="br-btn br-btn-outline-danger" data-action="cancel">Cancel booking</button>';
		} else if (permissions && permissions.cancel_reason) {
			html +=
				'<p class="br-muted br-manage-hint">' + escapeHtml(permissions.cancel_reason) + '</p>';
		}
		html += '</div>';
		return html;
	}

	function manageHtml() {
		return (
			'<div class="br-manage-card">' +
			(success ? '<p class="br-success" role="status">' + escapeHtml(success) + '</p>' : '') +
			(error ? '<p class="br-error" role="alert">' + escapeHtml(error) + '</p>' : '') +
			bookingSummaryHtml() +
			actionsHtml() +
			'<button type="button" class="br-btn-link br-manage-back" data-action="back">← Look up another booking</button>' +
			'</div>'
		);
	}

	function editHtml() {
		const dateLabel = editDate
			? formatShortDateLabel(editDate, { dateFormat: config.date_format })
			: '';
		let slotsBlock = '<p class="br-muted">Select a date to see available times.</p>';
		if (editDate) {
			if (slotsLoading) {
				slotsBlock = '<p class="br-muted">Loading times…</p>';
			} else if (!slots.length) {
				slotsBlock = '<p class="br-muted">No times available for this date.</p>';
			} else {
				slotsBlock =
					'<div class="br-slot-grid">' +
					slots
						.map((slot) => {
							const sel = editTime === slot.time;
							const label = slot.time_label || formatTimeLabel(slot.time, editDate, {
								timeFormat: config.time_format,
							});
							return (
								'<button type="button" class="br-slot' +
								(sel ? ' is-selected' : '') +
								'" data-time="' +
								escapeHtml(slot.time) +
								'">' +
								escapeHtml(label) +
								'</button>'
							);
						})
						.join('') +
					'</div>';
			}
		}

		return (
			'<div class="br-manage-card">' +
			'<h2 class="br-manage-card__title">Change appointment</h2>' +
			(error ? '<p class="br-error" role="alert">' + escapeHtml(error) + '</p>' : '') +
			bookingSummaryHtml() +
			'<div class="br-manage-edit-grid">' +
			'<div class="br-manage-calendar" data-calendar></div>' +
			'<div class="br-manage-slots">' +
			(dateLabel
				? '<p class="br-muted">Available times for ' + escapeHtml(dateLabel) + '</p>'
				: '') +
			slotsBlock +
			'</div></div>' +
			'<div class="br-manage-edit-actions">' +
			'<button type="button" class="br-btn br-btn-primary" data-action="save-edit"' +
			(loading || !editDate || !editTime ? ' disabled' : '') +
			'>Save changes</button>' +
			'<button type="button" class="br-btn br-btn--ghost" data-action="cancel-edit">Back</button>' +
			'</div></div>'
		);
	}

	function render() {
		let html = '<div class="br-manage-booking">';
		if (step === 'lookup') {
			html += lookupHtml();
		} else if (step === 'manage') {
			html += manageHtml();
		} else if (step === 'edit') {
			html += editHtml();
		}
		html += '</div>';
		root.innerHTML = html;
		bind();
	}

	function bind() {
		const form = root.querySelector('#br-manage-lookup-form');
		if (form) {
			form.addEventListener('submit', async (e) => {
				e.preventDefault();
				error = '';
				success = '';
				loading = true;
				render();
				const id = parseInt(form.querySelector('[name="booking_id"]').value, 10);
				const email = form.querySelector('[name="email"]').value.trim();
				try {
					const data = await api.post('/public/booking/manage/lookup', {
						booking_id: id,
						email,
					});
					booking = data.booking;
					permissions = data.permissions;
					verifiedEmail = email;
					step = 'manage';
				} catch (err) {
					error = err.message;
				} finally {
					loading = false;
					render();
				}
			});
		}

		root.querySelectorAll('[data-action]').forEach((btn) => {
			btn.addEventListener('click', async () => {
				const action = btn.getAttribute('data-action');
				error = '';
				success = '';

				if (action === 'back') {
					step = 'lookup';
					booking = null;
					permissions = null;
					verifiedEmail = '';
					unavailableDates = {};
					monthAvailabilityKey = '';
					calendarWidget = null;
					render();
					return;
				}

				if (action === 'edit') {
					editDate = booking.booking_date;
					editTime = booking.booking_time;
					unavailableDates = {};
					monthAvailabilityKey = '';
					calendarWidget = null;
					step = 'edit';
					render();
					loadSlots();
					return;
				}

				if (action === 'cancel-edit') {
					step = 'manage';
					calendarWidget = null;
					render();
					return;
				}

				if (action === 'cancel') {
					if (
						!window.confirm(
							'Cancel this booking? This cannot be undone.'
						)
					) {
						return;
					}
					loading = true;
					render();
					try {
						const data = await api.post(
							'/public/booking/manage/' + booking.id + '/cancel',
							{ email: verifiedEmail }
						);
						booking = data.booking;
						permissions = {
							can_edit: false,
							can_cancel: false,
							edit_reason: '',
							cancel_reason: '',
						};
						success = data.message || 'Booking cancelled.';
						step = 'manage';
					} catch (err) {
						error = err.message;
					} finally {
						loading = false;
						render();
					}
					return;
				}

				if (action === 'save-edit') {
					loading = true;
					render();
					try {
						const data = await api.put(
							'/public/booking/manage/' + booking.id + '/reschedule',
							{
								email: verifiedEmail,
								booking_date: editDate,
								booking_time: editTime,
							}
						);
						booking = data.booking;
						permissions = data.permissions;
						success = data.message || 'Appointment updated.';
						step = 'manage';
					} catch (err) {
						error = err.message;
					} finally {
						loading = false;
						render();
					}
				}
			});
		});

		const calHost = root.querySelector('[data-calendar]');
		if (calHost && step === 'edit') {
			calendarWidget = createCalendar(calHost, {
				selectedDate: editDate,
				timezone: config.timezone,
				isDateDisabled,
				onMonthChange: (year, month) => {
					loadMonthAvailability(year, month);
				},
				onSelect: (dateStr) => {
					editDate = dateStr;
					editTime = '';
					render();
					loadSlots();
				},
			});
		}

		root.querySelectorAll('.br-slot[data-time]').forEach((slotBtn) => {
			slotBtn.addEventListener('click', () => {
				editTime = slotBtn.getAttribute('data-time');
				render();
			});
		});
	}

	render();
}
